import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Card,
  Modal,
  Button,
  Table,
  Badge,
  Pagination,
  Spinner,
  TextInput,
  Select,
  Dropdown,
} from "flowbite-react";
import {
  HiEye,
  HiCheck,
  HiCheckCircle,
  HiDotsVertical,
  HiFilter,
  HiPlus,
  HiSearch,
  HiUserGroup,
  HiUsers,
} from "react-icons/hi";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import { useAllPermissions } from "../utils/usePermission";
import DashboardLayout from "../components/layout/DashboardLayout";

export default function Marketers() {
  let store = useContext(Store);
  let [baseUrl] = store.url;

  const [marketers, setMarketers] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [search, setSearch] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState(""); // "", "true", "false"
  const [accountTypeFilter, setAccountTypeFilter] = useState(""); // "", "staff", "external"
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(false);

  const loadMarketers = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("royal-token");
      const params = new URLSearchParams();

      params.append("page", page);
      params.append("limit", pagination.limit);

      if (search.trim() !== "") params.append("search", search.trim());
      if (verifiedFilter) params.append("verified", verifiedFilter);
      if (accountTypeFilter) params.append("accountType", accountTypeFilter);

      const url = `${baseUrl}/marketers?${params.toString()}`;
      const res = await apiRequest(url, "GET", null, token);

      setMarketers(res.data.data || []);
      setPagination(res.data.pagination || pagination);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMarketers(pagination.page);
    // eslint-disable-next-line
  }, [reload, baseUrl, pagination.page, verifiedFilter, accountTypeFilter]);

  const viewMarketer = async (id) => {
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/marketer/${id}`;
      const { data } = await apiRequest(url, "GET", null, token);
      setSelected(data);
      setOpenViewModal(true);
    } catch (err) {
      console.log(err.message);
    }
  };

  const verifyMarketer = async (id) => {
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/marketer/${id}`;
      await apiRequest(url, "PUT", { verified: true, status: "active" }, token);
      setReload(!reload);
      alert("Marketer verified successfully");
    } catch (err) {
      alert(err.message);
    }
  };

  const changePage = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (pagination.page === 1) loadMarketers(1);
  };

  const resetFilters = () => {
    setSearch("");
    setVerifiedFilter("");
    setAccountTypeFilter("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    setReload((prev) => !prev);
  };

  // Handle search input enter key to reload page 1 with new search
  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setPagination((prev) => ({ ...prev, page: 1 }));
      loadMarketers(1);
    }
  };

  const getIdentificationUrl = (url) => {
    if (!url) return "";
    return url.replace(/\.pdf(?=\?|#|$)/i, ".jpg");
  };

  const { canWriteMarketers, canReadMarketers } = useAllPermissions();

  const analytics = useMemo(() => {
    const visibleMarketers = marketers.length;
    const verifiedMarketers = marketers.filter((m) => m.verified).length;
    const pendingMarketers = marketers.filter((m) => !m.verified).length;
    const externalMarketers = marketers.filter(
      (m) => m.accountType === "external",
    ).length;

    return {
      visibleMarketers,
      verifiedMarketers,
      pendingMarketers,
      externalMarketers,
    };
  }, [marketers]);

  const AnalyticsCard = ({ title, value, colorClass, icon: Icon }) => (
    <Card className={`shadow-lg p-2 dark:bg-gray-800 border-l-4 ${colorClass}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            {title}
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">
            {value}
          </h2>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </Card>
  );

  return (
    <DashboardLayout active="marketers" contentClassName="lg:p-8">
      <h5 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
        <HiUserGroup className="w-8 h-8 mr-3 text-indigo-600" />
        Marketer Management
      </h5>

      <hr className="my-6 border-gray-200 dark:border-gray-700" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <AnalyticsCard
          title="Visible Marketers"
          value={analytics.visibleMarketers}
          colorClass="text-indigo-500 border-indigo-500"
          icon={HiUsers}
        />
        <AnalyticsCard
          title="Verified"
          value={analytics.verifiedMarketers}
          colorClass="text-green-500 border-green-500"
          icon={HiCheckCircle}
        />
        <AnalyticsCard
          title="Pending"
          value={analytics.pendingMarketers}
          colorClass="text-amber-500 border-amber-500"
          icon={HiFilter}
        />
        <AnalyticsCard
          title="External"
          value={analytics.externalMarketers}
          colorClass="text-cyan-500 border-cyan-500"
          icon={HiPlus}
        />
      </div>

      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            color="light"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="w-full sm:w-auto"
          >
            {filtersOpen ? "Hide Filters" : "Show Filters"}
          </Button>

          <div className="text-sm font-semibold text-gray-500 dark:text-gray-400">
            Total Records: {pagination.total}
          </div>
        </div>

        {filtersOpen && (
          <div className="mt-4 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-1">
              <h6 className="text-lg font-semibold text-gray-900 dark:text-white">
                Filters
              </h6>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Narrow marketers by name, email, verification status, and
                account type.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Search
                </label>
                <TextInput
                  type="text"
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Verification
                </label>
                <Select
                  value={verifiedFilter}
                  onChange={(e) => {
                    setVerifiedFilter(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="true">Verified</option>
                  <option value="false">Pending</option>
                </Select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Account Type
                </label>
                <Select
                  value={accountTypeFilter}
                  onChange={(e) => {
                    setAccountTypeFilter(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  <option value="">All Types</option>
                  <option value="staff">Staff</option>
                  <option value="external">External</option>
                </Select>
              </div>

              <div className="flex flex-col sm:flex-row lg:col-span-2 gap-2 lg:items-end">
                <Button
                  color="dark"
                  onClick={applyFilters}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  <HiSearch className="w-5 h-5 mr-2" />
                  Search
                </Button>
                <Button
                  color="light"
                  onClick={resetFilters}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Card className="shadow-2xl dark:bg-gray-800 overflow-x-auto min-h-[400px]">
        {loading ? (
          <div className="flex justify-center items-center min-h-[320px]">
            <Spinner aria-label="Loading marketers" size="xl" />
          </div>
        ) : marketers.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
            <HiUserGroup className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <h6 className="text-lg font-semibold text-gray-900 dark:text-white">
              No marketers found
            </h6>
            <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
              Adjust the filters or verify that marketer records have been
              created.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[58vh] scrollbar-always-visible">
            <Table hoverable striped>
              <Table.Head className="sticky top-0 z-10">
                <Table.HeadCell>Marketer</Table.HeadCell>
                <Table.HeadCell>Email</Table.HeadCell>
                <Table.HeadCell>Phone</Table.HeadCell>
                <Table.HeadCell>Type</Table.HeadCell>
                <Table.HeadCell>Verified</Table.HeadCell>
                <Table.HeadCell>
                  <span className="sr-only">Actions</span>
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y">
                {marketers.map((m) => (
                  <Table.Row
                    key={m.id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <Table.Cell className="whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                      {m.firstName} {m.lastName}
                    </Table.Cell>
                    <Table.Cell>{m.email || "N/A"}</Table.Cell>
                    <Table.Cell>{m.phone || "N/A"}</Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={m.accountType === "staff" ? "info" : "success"}
                        className="capitalize flex items-center justify-center text-center"
                      >
                        {m.accountType || "N/A"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={m.verified ? "success" : "warning"}
                        className="capitalize flex items-center justify-center text-center"
                      >
                        {m.verified ? "Verified" : "Pending"}
                      </Badge>
                    </Table.Cell>

                    <Table.Cell>
                      <Dropdown
                        label={<HiDotsVertical className="w-5 h-5" />}
                        inline
                        arrowIcon={false}
                      >
                        <Dropdown.Item
                          icon={HiEye}
                          onClick={() => viewMarketer(m.id)}
                          disabled={!canReadMarketers}
                        >
                          View Marketer
                        </Dropdown.Item>

                        {!m.verified && (
                          <Dropdown.Item
                            icon={HiCheck}
                            onClick={() => verifyMarketer(m.id)}
                            disabled={!canWriteMarketers}
                          >
                            Verify Marketer
                          </Dropdown.Item>
                        )}
                      </Dropdown>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      <div className="flex overflow-x-auto sm:justify-center mt-6 mb-2">
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={(p) => changePage(p)}
          showIcons
        />
      </div>

      {/* View Marketer Modal */}
      <Modal
        show={openViewModal}
        onClose={() => setOpenViewModal(false)}
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          // The backdrop needs a high z-index too
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>Marketer Details</Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "450px",
          }}
        >
          {selected && (
            <div className="p-4 space-y-6">
              {/* 1. Personal & Contact Information */}
              <div className="border-b pb-4">
                <h2 className="text-2xl font-extrabold text-primary mb-3 flex items-center gap-2">
                  {/* User Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-user w-6 h-6 text-indigo-500"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  {selected.firstName || selected.lastName
                    ? `${selected.firstName || ""} ${selected.lastName || ""}`
                    : "Marketer Profile"}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {/* Email */}
                  {selected.email && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {/* Mail Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-mail w-5 h-5 text-gray-500"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 6-10 7L2 6" />
                      </svg>
                      <span
                        className="font-medium truncate"
                        title={selected.email}
                      >
                        {selected.email}
                      </span>
                    </div>
                  )}
                  {/* Phone */}
                  {selected.phone && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {/* Phone Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-phone w-5 h-5 text-gray-500"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <span className="font-medium">{selected.phone}</span>
                    </div>
                  )}
                  {/* Address */}
                  {selected.address && (
                    <div className="flex items-start gap-2 col-span-full p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {/* Home Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-home w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-400">
                        {selected.address}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 2. Account Status Indicators */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b">
                {/* Account Type */}
                {selected.accountType && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {/* Users Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-users w-4 h-4 text-blue-500"
                      >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      Account Type
                    </p>
                    <p className="font-semibold capitalize text-base">
                      {selected.accountType}
                    </p>
                  </div>
                )}
                {/* Status */}
                {selected.status && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {/* Clipboard Check Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-clipboard-check w-4 h-4 text-purple-500"
                      >
                        <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <path d="m9 14 2 2 4-4" />
                      </svg>
                      Status
                    </p>
                    {/* Simple Badge Styling based on status (e.g., active/inactive) */}
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${
                        selected.status === "active"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                      }`}
                    >
                      {selected.status}
                    </span>
                  </div>
                )}
                {/* Verified */}
                {typeof selected.verified !== "undefined" && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {/* Check/X Circle Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`lucide ${
                          selected.verified ? "text-green-600" : "text-red-600"
                        } w-4 h-4`}
                      >
                        {selected.verified ? (
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M9 22 4 17l1.41-1.41" />
                        ) : (
                          <path d="M10 10l4 4M10 14l4-4M22 12A10 10 0 1 1 12 2a10 10 0 0 1 10 10z" />
                        )}
                      </svg>
                      Verified
                    </p>
                    <p
                      className={`font-semibold text-base ${
                        selected.verified ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {selected.verified ? "Yes" : "No"}
                    </p>
                  </div>
                )}
                {/* Type of Identification */}
                {selected.typeOfIdentification && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {/* Credit Card Icon (Placeholder for ID Type) */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-credit-card w-4 h-4 text-teal-500"
                      >
                        <rect width="20" height="14" x="2" y="5" rx="2" />
                        <line x1="2" x2="22" y1="10" y2="10" />
                      </svg>
                      ID Type
                    </p>
                    <p className="font-medium text-base">
                      {selected.typeOfIdentification}
                    </p>
                  </div>
                )}
              </div>

              {/* 3. Bank and Image Details */}
              <div className="space-y-6">
                {/* Bank Details */}
                {selected.bankDetails && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg shadow-md border-l-4 border-blue-500 dark:border-blue-400">
                    <h3 className="text-lg font-semibold flex items-center gap-2 mb-3 text-blue-800 dark:text-blue-200">
                      {/* Banknote Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-banknote w-5 h-5"
                      >
                        <rect width="20" height="14" x="2" y="5" rx="2" />
                        <circle cx="12" cy="12" r="3" />
                        <path d="M7 12h.01M17 12h.01" />
                      </svg>
                      Bank Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {selected.bankDetails.bankName && (
                        <p className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-500 dark:text-gray-400">
                            Bank:
                          </span>{" "}
                          <span className="font-bold">
                            {selected.bankDetails.bankName}
                          </span>
                        </p>
                      )}
                      {selected.bankDetails.accountName && (
                        <p className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-500 dark:text-gray-400">
                            Acct Name:
                          </span>{" "}
                          <span className="font-bold">
                            {selected.bankDetails.accountName}
                          </span>
                        </p>
                      )}
                      {selected.bankDetails.accountNumber && (
                        <p className="text-gray-700 dark:text-gray-300 col-span-full">
                          <span className="font-medium text-gray-500 dark:text-gray-400">
                            Account Number:
                          </span>{" "}
                          <span className="font-mono text-lg text-blue-600 dark:text-blue-400">
                            {selected.bankDetails.accountNumber}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Identification Image */}
                {selected.identification && (
                  <div className="space-y-3 pt-4 border-t">
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      {/* Scan Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-scan w-5 h-5 text-purple-500"
                      >
                        <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                        <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                        <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                        <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                      </svg>
                      Identification Document
                    </h3>

                    <div
                      className="group relative w-full max-w-xs overflow-hidden rounded-xl shadow-lg border border-gray-300 dark:border-gray-700 cursor-pointer transition-transform duration-300 hover:scale-[1.02] hover:shadow-xl"
                      onClick={() =>
                        window.open(
                          getIdentificationUrl(selected.identification),
                          "_blank"
                        )
                      }
                    >
                      <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <img
                          src={getIdentificationUrl(selected.identification)}
                          alt="Marketer Identification Document"
                          className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-60"
                        />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="text-center text-white p-4">
                          {/* ExternalLink Icon */}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-external-link w-8 h-8 mx-auto mb-1 text-white"
                          >
                            <path d="M15 3h6v6" />
                            <path d="M10 14 21 3" />
                            <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
                          </svg>
                          <p className="text-sm font-bold">
                            Open Full Document
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Profile Image (Simple, cleaner version) */}
                {selected.image && (
                  <div className="space-y-2 pt-4 border-t">
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      {/* User Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-user w-5 h-5 text-purple-500"
                      >
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Profile Image
                    </h3>
                    <div className="w-24 h-24 relative cursor-pointer overflow-hidden rounded-full shadow-md">
                      <img
                        src={selected.image}
                        alt="Profile"
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                        onClick={() => window.open(selected.image, "_blank")}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button color="gray" onClick={() => setOpenViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
}
